import type { TooltipContent } from "../render/RenderContext.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import { rectContains, type Rect, type Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawTriangle } from "../themes/glyphs.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { ScrollableBase, type ScrollableOptions } from "./ScrollableBase.js";
import type { SelectionMode } from "./ListBox.js";
import {
  flattenTree,
  isNodeEnabled,
  nodeChildren,
  rowIndexOf,
  walkAll,
  type TreeNodeSpec,
  type TreeRow,
} from "./TreeNode.js";

export interface TreeViewOptions<T = unknown> extends ScrollableOptions {
  /** Single by default, matching `ListBox`. */
  selectionMode?: SelectionMode;
  nodes?: TreeNodeSpec<T>[];
  /** Horizontal step per level, in px. */
  indent?: number;
  /** Dotted lines joining siblings and parents, as Classic Explorer had. */
  showLines?: boolean;
  /** Extend those lines to the root nodes. */
  showRootLines?: boolean;
  /** Give every node a checkbox. */
  checkBoxes?: boolean;
  itemHeight?: number;
  font?: FontSpec;
  onSelectionChanged?: () => void;
}

/** How long a run of keystrokes counts as one type-ahead search. */
const TYPE_AHEAD_MS = 800;
const EXPANDER_SIZE = 16;
const ICON_SIZE = 16;
const GAP = 4;

/**
 * A hierarchical list with expandable nodes.
 *
 * Built on the same scrollable base as `ListBox`: the tree is flattened to the
 * rows that are currently visible, and those rows are virtualized, so an open
 * branch of ten thousand nodes costs the same to paint as ten.
 */
export class TreeView<T = unknown> extends ScrollableBase {
  #nodes: TreeNodeSpec<T>[];
  #rows: TreeRow<T>[] = [];
  #rowsStale = true;
  /**
   * Every selected node. `#current` is the one of them the keyboard and the focus ring are on,
   * and `#anchor` is where a shift-range measures from - the same three-part model `ListBox`
   * uses, because a person moving between the two should not have to learn a second one.
   */
  #selected = new Set<TreeNodeSpec<T>>();
  #current: TreeNodeSpec<T> | null = null;
  #anchor: TreeNodeSpec<T> | null = null;
  #hover = -1;
  #itemHeight: number | null;
  #font: FontSpec | null;
  #maxWidth = -1;
  #typeAhead = "";
  #typeAheadAt = 0;

  selectionMode: SelectionMode;
  indent: number;
  showLines: boolean;
  showRootLines: boolean;
  checkBoxes: boolean;

  constructor(options: TreeViewOptions<T> = {}) {
    super({ width: 240, height: 200, tabStop: true, ...options });
    this.#nodes = options.nodes ?? [];
    this.selectionMode = options.selectionMode ?? "single";
    this.indent = options.indent ?? 18;
    this.showLines = options.showLines ?? false;
    this.showRootLines = options.showRootLines ?? true;
    this.checkBoxes = options.checkBoxes ?? false;
    this.#itemHeight = options.itemHeight ?? null;
    this.#font = options.font ?? null;
    if (options.onSelectionChanged) this.on("selectionchanged", options.onSelectionChanged);

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#setHover(this.rowIndexAt({ x: e.x, y: e.y })));
    this.on("pointerleave", () => this.#setHover(-1));
    this.on("doubleclick", (e) => this.#onDoubleClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  // --- model ------------------------------------------------------------

  get nodes(): TreeNodeSpec<T>[] {
    return this.#nodes;
  }
  set nodes(v: TreeNodeSpec<T>[]) {
    this.#nodes = v;
    this.#selected.clear();
    this.#current = null;
    this.#anchor = null;
    this.refresh();
  }

  /** Re-derive the visible rows. Call after mutating nodes directly. */
  refresh(): void {
    this.#rowsStale = true;
    this.#maxWidth = -1;
    this.updateScrollBars();
    this.clampScroll();
    this.invalidate();
  }

  /** The visible rows, rebuilt on demand. */
  get rows(): readonly TreeRow<T>[] {
    if (this.#rowsStale) {
      this.#rows = flattenTree(this.#nodes);
      this.#rowsStale = false;
    }
    return this.#rows;
  }

  get rowCount(): number {
    return this.rows.length;
  }

  get itemHeight(): number {
    return this.#itemHeight ?? this.desktop?.theme.metrics.listItemHeight ?? 22;
  }
  set itemHeight(v: number | null) {
    this.#itemHeight = v;
    this.refresh();
  }

  /**
   * The node the keyboard is on: the only selected one in single mode, and the most recently
   * touched of them in multiple mode.
   */
  get selectedNode(): TreeNodeSpec<T> | null {
    return this.#current;
  }
  set selectedNode(v: TreeNodeSpec<T> | null) {
    this.#anchor = v;
    this.#setSelection(v ? [v] : [], v);
  }

  /** Every selected node, in the order the rows are shown. */
  get selectedNodes(): TreeNodeSpec<T>[] {
    if (this.#selected.size === 0) return [];
    return this.rows.map((row) => row.node).filter((node) => this.#selected.has(node));
  }
  /**
   * Filtered by `selectionMode` rather than obeyed literally, so a caller that does not know
   * how the control is configured cannot put it into a state it could not reach by hand.
   */
  set selectedNodes(v: readonly TreeNodeSpec<T>[]) {
    const wanted =
      this.selectionMode === "none" ? []
      : this.selectionMode === "single" ? v.slice(0, 1)
      : v;
    this.#anchor = wanted[wanted.length - 1] ?? null;
    this.#setSelection(wanted, wanted[wanted.length - 1] ?? null);
  }

  isSelected(node: TreeNodeSpec<T>): boolean {
    return this.#selected.has(node);
  }

  clearSelection(): void {
    this.#setSelection([], null);
  }

  /** Index of the node the keyboard is on among the visible rows, or -1. */
  get selectedIndex(): number {
    return rowIndexOf(this.rows, this.#current);
  }
  set selectedIndex(v: number) {
    this.selectedNode = this.rows[v]?.node ?? null;
  }

  /** Indices of every selected node among the visible rows. */
  get selectedIndices(): number[] {
    const indices: number[] = [];
    this.rows.forEach((row, i) => {
      if (this.#selected.has(row.node)) indices.push(i);
    });
    return indices;
  }

  #setSelection(nodes: readonly TreeNodeSpec<T>[], current: TreeNodeSpec<T> | null): void {
    const next = new Set(nodes);
    const same =
      next.size === this.#selected.size &&
      [...next].every((n) => this.#selected.has(n)) &&
      current === this.#current;
    if (same) return;
    this.#selected = next;
    this.#current = current;
    this.invalidate();
    this.emit("selectionchanged");
  }

  /** The visible rows between two nodes, inclusive, for a shift-click or shift-arrow. */
  #range(from: TreeNodeSpec<T> | null, to: TreeNodeSpec<T>): TreeNodeSpec<T>[] {
    const rows = this.rows;
    const a = from ? rowIndexOf(rows, from) : -1;
    const b = rowIndexOf(rows, to);
    if (b < 0) return [];
    if (a < 0) return [to];
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return rows.slice(lo, hi + 1).map((row) => row.node).filter(isNodeEnabled);
  }

  /** Apply a click or a keystroke to the selection, honoring the modifiers. */
  #selectRow(node: TreeNodeSpec<T>, modifiers: { ctrlKey: boolean; shiftKey: boolean }): void {
    if (this.selectionMode === "none") return;
    if (this.selectionMode === "single") {
      this.selectedNode = node;
      return;
    }

    if (modifiers.shiftKey) {
      const range = this.#range(this.#anchor, node);
      this.#setSelection(modifiers.ctrlKey ? [...this.#selected, ...range] : range, node);
      return;
    }
    if (modifiers.ctrlKey) {
      const next = new Set(this.#selected);
      if (next.has(node)) next.delete(node);
      else next.add(node);
      this.#anchor = node;
      this.#setSelection([...next], node);
      return;
    }
    this.#anchor = node;
    this.#setSelection([node], node);
  }

  // --- expanding --------------------------------------------------------

  /** Open a node, running its loader the first time if it has one. */
  expand(node: TreeNodeSpec<T>): void {
    if (node.expanded) return;
    if (node.loadChildren && node.children === undefined) {
      node.children = node.loadChildren(node);
    }
    if (nodeChildren(node).length === 0) return;
    node.expanded = true;
    this.refresh();
    this.emit("expandedchanged");
  }

  collapse(node: TreeNodeSpec<T>): void {
    if (!node.expanded) return;
    node.expanded = false;
    // Selecting a node then collapsing its parent would strand the selection on rows nobody
    // can see, so anything that just went out of sight is replaced by the branch that
    // swallowed it - and whatever was selected elsewhere is left alone.
    const stranded = [...this.#selected].filter((n) => this.#isDescendantOf(n, node));
    if (stranded.length > 0) {
      const kept = [...this.#selected].filter((n) => !stranded.includes(n));
      this.#anchor = node;
      this.#setSelection([...kept, node], node);
    }
    this.refresh();
    this.emit("expandedchanged");
  }

  toggle(node: TreeNodeSpec<T>): void {
    if (node.expanded) this.collapse(node);
    else this.expand(node);
  }

  expandAll(): void {
    walkAll(this.#nodes, (n) => {
      if (n.loadChildren && n.children === undefined) n.children = n.loadChildren(n);
      if (nodeChildren(n).length > 0) n.expanded = true;
    });
    this.refresh();
    this.emit("expandedchanged");
  }

  collapseAll(): void {
    walkAll(this.#nodes, (n) => {
      n.expanded = false;
    });
    this.refresh();
    this.emit("expandedchanged");
  }

  /** Open every ancestor of `node` so its row exists, then scroll to it. */
  reveal(node: TreeNodeSpec<T>): void {
    const path: TreeNodeSpec<T>[] = [];
    const find = (list: readonly TreeNodeSpec<T>[], trail: TreeNodeSpec<T>[]): boolean => {
      for (const n of list) {
        if (n === node) {
          path.push(...trail);
          return true;
        }
        if (find(nodeChildren(n), [...trail, n])) return true;
      }
      return false;
    };
    if (!find(this.#nodes, [])) return;
    for (const ancestor of path) {
      if (ancestor.loadChildren && ancestor.children === undefined) {
        ancestor.children = ancestor.loadChildren(ancestor);
      }
      ancestor.expanded = true;
    }
    this.refresh();
    this.ensureVisible(rowIndexOf(this.rows, node));
  }

  #isDescendantOf(node: TreeNodeSpec<T>, ancestor: TreeNodeSpec<T>): boolean {
    let found = false;
    walkAll(nodeChildren(ancestor), (n) => {
      if (n === node) found = true;
    });
    return found;
  }

  // --- geometry ---------------------------------------------------------

  protected override onEnvironmentChanged(): void {
    // The cached widest-row width is measured against the old font.
    this.#maxWidth = -1;
    super.onEnvironmentChanged();
  }

  protected override contentSize(theme: Theme): Size {
    const pad = theme.metrics.listPadding;
    return {
      width: this.#contentWidth(theme) + pad.left + pad.right,
      height: this.rowCount * this.itemHeight + pad.top + pad.bottom,
    };
  }

  /** Widest row including its indent, measured once and cached. */
  #contentWidth(theme: Theme): number {
    if (this.#maxWidth >= 0) return this.#maxWidth;
    const desktop = this.desktop;
    if (!desktop) return 0;
    const font = this.#font ?? theme.metrics.font;
    let max = 0;
    for (const row of this.rows) {
      const w = this.#textLeft(row) + desktop.rc.measureText(row.node.text, font).width;
      if (w > max) max = w;
    }
    this.#maxWidth = Math.ceil(max) + theme.metrics.columnPadding;
    return this.#maxWidth;
  }

  /** X offset of a row's text, from the content origin. */
  #textLeft(row: TreeRow<T>): number {
    const base = (row.depth + (this.showRootLines ? 1 : 0)) * this.indent;
    let x = base;
    if (this.checkBoxes) x += EXPANDER_SIZE + GAP;
    if (row.node.icon) x += ICON_SIZE + GAP;
    return x + GAP;
  }

  /** Row index at a point in local coordinates, or -1. */
  rowIndexAt(p: { x: number; y: number }): number {
    const theme = this.desktop?.theme;
    if (!theme) return -1;
    const view = this.viewportRect;
    if (!rectContains(view, p)) return -1;
    const pad = theme.metrics.listPadding;
    const y = p.y - view.y - pad.top + this.scrollOffsetY;
    const index = Math.floor(y / this.itemHeight);
    return index >= 0 && index < this.rowCount ? index : -1;
  }

  /** Row rectangle in content coordinates. */
  rowContentRect(index: number, theme: Theme): Rect {
    const pad = theme.metrics.listPadding;
    return {
      x: 0,
      y: pad.top + index * this.itemHeight,
      width: this.#contentWidth(theme),
      height: this.itemHeight,
    };
  }

  /** The expander box for a row, in content coordinates. */
  expanderContentRect(index: number, theme: Theme): Rect {
    const row = this.rows[index];
    const r = this.rowContentRect(index, theme);
    if (!row) return { x: 0, y: r.y, width: 0, height: r.height };
    const center = row.depth * this.indent + this.indent / 2;
    return {
      x: center - EXPANDER_SIZE / 2,
      y: r.y + (r.height - EXPANDER_SIZE) / 2,
      width: EXPANDER_SIZE,
      height: EXPANDER_SIZE,
    };
  }

  /** The checkbox for a row, in content coordinates. */
  checkContentRect(index: number, theme: Theme): Rect {
    const row = this.rows[index];
    const r = this.rowContentRect(index, theme);
    const size = theme.metrics.checkBoxSize;
    const x = (row ? row.depth + (this.showRootLines ? 1 : 0) : 0) * this.indent + GAP;
    return { x, y: r.y + (r.height - size) / 2, width: size, height: size };
  }

  ensureVisible(index: number): void {
    const theme = this.desktop?.theme;
    if (!theme || index < 0 || index >= this.rowCount) return;
    const r = this.rowContentRect(index, theme);
    this.scrollIntoView({ x: this.scrollOffsetX, y: r.y, width: 0, height: r.height });
  }

  override getTooltip(p: { x: number; y: number }): TooltipContent | null {
    const i = this.rowIndexAt(p);
    return i >= 0 ? (this.rows[i]!.node.text ?? null) : this.tooltip;
  }

  // --- input ------------------------------------------------------------

  #setHover(index: number): void {
    if (this.#hover === index) return;
    this.#hover = index;
    this.invalidate();
  }

  /** Convert a local point to content coordinates for hit-testing parts. */
  #toContent(p: { x: number; y: number }): { x: number; y: number } {
    const view = this.viewportRect;
    return { x: p.x - view.x + this.scrollOffsetX, y: p.y - view.y + this.scrollOffsetY };
  }

  #onPointerDown(e: MouseEventArgs): void {
    const theme = this.desktop?.theme;
    if (!theme) return;
    const index = this.rowIndexAt({ x: e.x, y: e.y });
    this.focus();
    if (index < 0) return;
    const row = this.rows[index]!;
    const cp = this.#toContent({ x: e.x, y: e.y });
    e.handled = true;

    // The expander swallows the click: clicking it must not also select.
    if (row.expandable && rectContains(this.expanderContentRect(index, theme), cp)) {
      this.toggle(row.node);
      return;
    }
    if (this.checkBoxes && rectContains(this.checkContentRect(index, theme), cp)) {
      if (isNodeEnabled(row.node)) {
        row.node.checked = !row.node.checked;
        this.invalidate();
        this.emit("checkedchanged");
      }
      return;
    }
    if (!isNodeEnabled(row.node)) return;
    // A press on something already selected leaves the selection alone, so a drag can carry all
    // of it: collapsing to one row here would make a multi-row drag impossible to start.
    if (this.#selected.has(row.node) && !e.ctrlKey && !e.shiftKey && this.#selected.size > 1) {
      this.#current = row.node;
      this.#anchor = row.node;
      this.invalidate();
      return;
    }
    this.#selectRow(row.node, e);
  }

  #onDoubleClick(e: MouseEventArgs): void {
    const index = this.rowIndexAt({ x: e.x, y: e.y });
    if (index < 0) return;
    const row = this.rows[index]!;
    if (!isNodeEnabled(row.node)) return;
    e.handled = true;
    if (row.expandable) this.toggle(row.node);
    else this.emit("itemactivate", index);
  }

  #move(delta: number, extend = false): void {
    const n = this.rowCount;
    if (n === 0) return;
    const from = this.selectedIndex;
    const next = from < 0 ? (delta > 0 ? 0 : n - 1) : Math.max(0, Math.min(n - 1, from + delta));
    this.#goTo(next, extend);
  }

  /** Select a row by absolute index and scroll it into view. */
  #goTo(index: number, extend = false): void {
    if (this.rowCount === 0) return;
    const next = Math.max(0, Math.min(this.rowCount - 1, index));
    const node = this.rows[next]?.node;
    if (!node) return;
    // Shift keeps the anchor where it was, so holding it sweeps a range rather than dragging a
    // one-row selection along.
    if (extend && this.selectionMode === "multiple") this.#setSelection(this.#range(this.#anchor, node), node);
    else this.selectedNode = node;
    this.ensureVisible(next);
  }

  #page(): number {
    return Math.max(1, Math.floor(this.viewportRect.height / this.itemHeight) - 1);
  }

  #onKeyDown(e: KeyEventArgs): void {
    // Ctrl+A is the one chord this control wants; the rest belong to whatever is around it.
    if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
      if (this.selectionMode !== "multiple") return;
      this.#setSelection(this.rows.map((r) => r.node).filter(isNodeEnabled), this.#current);
      e.handled = true;
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    const index = this.selectedIndex;
    const row = index >= 0 ? this.rows[index] : undefined;
    const extend = e.shiftKey;

    switch (e.key) {
      case "ArrowDown":
        this.#move(1, extend);
        e.handled = true;
        return;
      case "ArrowUp":
        this.#move(-1, extend);
        e.handled = true;
        return;
      case "Home":
        this.#goTo(0, extend);
        e.handled = true;
        return;
      case "End":
        this.#goTo(this.rowCount - 1, extend);
        e.handled = true;
        return;
      case "PageDown":
        this.#move(this.#page(), extend);
        e.handled = true;
        return;
      case "PageUp":
        this.#move(-this.#page(), extend);
        e.handled = true;
        return;
      case "ArrowRight":
        // Open a closed branch; step into an open one. Explorer's behavior.
        if (row) {
          if (row.expandable && !row.node.expanded) this.expand(row.node);
          else if (row.node.expanded) this.#move(1);
          e.handled = true;
        }
        return;
      case "ArrowLeft":
        // Close an open branch; otherwise step out to the parent.
        if (row) {
          if (row.node.expanded) this.collapse(row.node);
          else if (row.parent) {
            this.selectedNode = row.parent;
            this.ensureVisible(this.selectedIndex);
          }
          e.handled = true;
        }
        return;
      case "Enter":
        if (row) {
          this.emit("itemactivate", index);
          e.handled = true;
        }
        return;
      case " ":
        if (row && this.checkBoxes && isNodeEnabled(row.node)) {
          row.node.checked = !row.node.checked;
          this.invalidate();
          this.emit("checkedchanged");
        } else if (row?.expandable) {
          this.toggle(row.node);
        }
        e.handled = true;
        return;
      case "*":
        this.expandAll();
        e.handled = true;
        return;
      default:
        break;
    }
    if (e.key.length === 1 && this.#typeAheadFind(e.key)) e.handled = true;
  }

  /** Jump to the next visible row whose text starts with the typed prefix. */
  #typeAheadFind(char: string): boolean {
    const now = typeof performance === "undefined" ? 0 : performance.now();
    if (now - this.#typeAheadAt > TYPE_AHEAD_MS) this.#typeAhead = "";
    this.#typeAheadAt = now;
    const cycling = this.#typeAhead === char;
    this.#typeAhead = cycling ? char : this.#typeAhead + char;
    const prefix = this.#typeAhead.toLowerCase();

    const n = this.rowCount;
    if (n === 0) return false;
    const start = this.#typeAhead.length === 1 ? this.selectedIndex + 1 : 0;
    for (let i = 0; i < n; i++) {
      const index = (((start + i) % n) + n) % n;
      if (this.rows[index]!.node.text.toLowerCase().startsWith(prefix)) {
        this.selectedIndex = index;
        this.ensureVisible(index);
        return true;
      }
    }
    return false;
  }

  // --- painting ---------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    theme.drawListBackground(rc, this.localRect, state);

    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;
    const pad = theme.metrics.listPadding;
    const font = this.#font ?? theme.metrics.font;
    const rowHeight = this.itemHeight;
    const rows = this.rows;
    const p = theme.palette;

    rc.clipped(view, () => {
      // Virtualize: only the rows overlapping the viewport are drawn.
      const first = Math.max(0, Math.floor((this.scrollOffsetY - pad.top) / rowHeight));
      const last = Math.min(
        rows.length - 1,
        Math.ceil((this.scrollOffsetY + view.height - pad.top) / rowHeight),
      );
      const width = Math.max(view.width, this.#contentWidth(theme));
      const ox = view.x - this.scrollOffsetX;
      const oy = view.y + pad.top - this.scrollOffsetY;

      for (let i = first; i <= last; i++) {
        const row = rows[i]!;
        const enabled = isNodeEnabled(row.node);
        const band: Rect = { x: ox, y: oy + i * rowHeight, width, height: rowHeight };
        const rowState: VisualState = {
          ...state,
          hovered: this.#hover === i,
          selected: this.#selected.has(row.node),
          disabled: !enabled,
          active: this.focused,
          focused: row.node === this.#current && this.focused,
        };

        if (rowState.selected || rowState.hovered) theme.drawListItem(rc, band, rowState);
        if (this.showLines) this.#paintGuides(rc, theme, row, band);

        if (row.expandable) {
          const er = this.expanderContentRect(i, theme);
          this.#paintExpander(
            rc,
            theme,
            { x: ox + er.x, y: band.y + (band.height - er.height) / 2, width: er.width, height: er.height },
            row.node.expanded === true,
          );
        }

        let x = ox + (row.depth + (this.showRootLines ? 1 : 0)) * this.indent + GAP;
        if (this.checkBoxes) {
          const cr = this.checkContentRect(i, theme);
          theme.drawCheckBox(
            rc,
            {
              x: ox + cr.x,
              y: band.y + (band.height - cr.height) / 2,
              width: cr.width,
              height: cr.height,
            },
            {
              hovered: false,
              pressed: false,
              focused: false,
              disabled: !enabled,
              checked: row.node.checked === true,
            },
          );
          x = ox + cr.x + cr.width + GAP;
        }
        if (row.node.icon) {
          rc.drawTexture(row.node.icon, {
            x,
            y: band.y + (band.height - ICON_SIZE) / 2,
            width: ICON_SIZE,
            height: ICON_SIZE,
          });
          x += ICON_SIZE + GAP;
        }

        const ink = enabled ? theme.listItemTextColor(rowState) : p.textDisabled;
        rc.drawTextInRect(
          row.node.text,
          { x, y: band.y, width: Math.max(0, band.x + band.width - x), height: band.height },
          font,
          ink,
        );
        if (rowState.focused && !rowState.selected) theme.drawFocusRing(rc, band);
      }
    });
  }

  /** A boxed +/- on bevelled themes, a chevron on flat ones. */
  #paintExpander(rc: RenderContext, theme: Theme, r: Rect, open: boolean): void {
    const p = theme.palette;
    if (theme.metrics.scrollBarArrows) {
      const box: Rect = { x: r.x + 3, y: r.y + 3, width: r.width - 6, height: r.height - 6 };
      rc.drawRect(box, { fill: p.fieldBg, borderWidth: 1, borderColor: p.controlBorder });
      const cx = Math.round(box.x + box.width / 2);
      const cy = Math.round(box.y + box.height / 2);
      rc.fillRect({ x: cx - 3, y: cy, width: 7, height: 1 }, p.text);
      if (!open) rc.fillRect({ x: cx, y: cy - 3, width: 1, height: 7 }, p.text);
      return;
    }
    drawTriangle(rc, r, open ? "down" : "right", p.textSubtle, 4);
  }

  /** Dotted guides joining a node to its parent and its siblings. */
  #paintGuides(rc: RenderContext, theme: Theme, row: TreeRow<T>, band: Rect): void {
    const ink = theme.palette.gridLine;
    const half = Math.round(band.height / 2);
    // Every other pixel, which is what makes the run read as dotted.
    for (let level = 0; level < row.guides.length; level++) {
      if (!row.guides[level]) continue;
      const gx = Math.round(band.x + level * this.indent + this.indent / 2);
      rc.fillChecker({ x: gx, y: band.y, width: 1, height: band.height }, ink);
    }

    const x = Math.round(band.x + row.depth * this.indent + this.indent / 2);
    // The elbow stops halfway down on a last child, so the run ends there.
    rc.fillChecker({ x, y: band.y, width: 1, height: row.last ? half : band.height }, ink);
    rc.fillChecker({ x, y: band.y + half, width: Math.ceil(this.indent / 2), height: 1 }, ink);
  }

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    this.paintScrollCorner(rc, theme);
  }
}
